<template>
    <div class="claim-page page-padding">
        <div class="top-image-form">
            <h1>
                {{ source_origin == "gf_insurance" ? $t('f_top_image_r1_gf') : $t('f_top_image_r1') }}
            </h1>
        </div>

        <div ref="formBlock" class="left-right-wrap">
            <div class="left">
                <claim-step
                    :current-step="step"
                    :step-count="short_form ? 2 : 4"
                    :step-names="[
                        $t('f_r1_stepname_1'),
                        $t('f_r1_stepname_2'),
                        $t('f_r1_stepname_3'),
                        $t('f_r1_stepname_4')]" />
                <r1s1flight v-show="step === 1" />
                <r1s2case v-if="step === 2" />
                <r1s3check v-if="step === 3" />
                <r1s4receipt v-if="step === 4" />
            </div>

            <div v-if="!$device.mobile()" class="right">
                <right-part-cases
                    :total_clients="$props.total_clients"
                    :average_saved="$props.average_saved"
                    :total_compensation="$props.total_compensation"
                    :reviews="$props.reviews"
                    :score="$props.score" />
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import {mapGetters} from "vuex";
import {mapFields} from 'vuex-map-fields'
import ClaimStep from '@components/common/ClaimStep'
import R1s1flight from '@components/round1/R1s1flight'
import RightPartCases from '@components/common/RightPartCases'

export default {
  components: {
    ClaimStep,
    R1s1flight,
    RightPartCases,
    R1s2case: () => import(/* webpackChunkName: 'R1s2case', webpackPreload: true */ "@components/round1/R1s2case"),
    R1s3check: () => import(/* webpackChunkName: 'R1s3check', webpackPreload: true */ "@components/round1/R1s3check"),
    R1s4receipt: () => import(/* webpackChunkName: 'R1s4receipt' webpackPreload: true */ "@components/round1/R1s4receipt"),
  },
  props: ['source_origin', 'dep_apt', 'arr_apt', 'total_compensation', 'total_clients', 'average_saved', 'reviews',
    'score', 'tduid', 'pc_guid', 'short_form'],
  data() {
    return {
      prefilledData: {},
      compensationData: {},
    };
  },
  computed: {
    ...mapFields({
      locale: 'locale',
      step: 'round1.step',
      people: 'round1.complaint.people',
      compensation_amount: 'round1.compensation.amount_in_eur',
      covered_by_rules: 'round1.compensation.rules',
      complaint_id: 'round1.receipt.id'
    }),
    ...mapGetters({
      delaysMapperForRegular: 'round1/delaysMapperRegular',
      defaultCountry: 'round1/defaultCountry',
      defaultCurrency: 'round1/defaultCurrency',
    }),
  },
  watch: {
    step: {
      handler: function (newVal) {
        this.$store.dispatch('round1/trackFormPageVisit');
        if (newVal > 1) {
          this.scrollToFormTop()
        }
      },
      immediate: true
    }
  },
  async created() {
    // reset store
    this.$store.commit('round1/RESET_STORE')

    // default residence country from domain locale (overridden by prefilled data below)
    this.$store.commit('round1/updateField', {
      path: 'complaint.macda_country_id',
      value: this.defaultCountry
    })

    if (this.pc_guid) {
      // TODO: refactor this, take too long, blocking translations
      await this.fetchPossibleComplaintData(this.pc_guid)
    }

    // set complaint.source_origin
    this.$store.commit('round1/updateField', {
      path: 'complaint.source_origin',
      value: this.source_origin
    })
    // set complaint.tduid
    this.$store.commit('round1/updateField', {
      path: 'complaint.tduid',
      value: this.tduid
    })

    if (this.dep_apt !== undefined) {
      this.getAirport(this.dep_apt, "departure_airport")
    }
    if (this.arr_apt !== undefined) {
      this.getAirport(this.arr_apt, "arrival_airport")
    }
    if (this.arr_apt !== undefined && this.dep_apt !== undefined) {
      this.getPossibleCompensation();
    }
    window.dataLayer = window.dataLayer || []
    if (this.step === 1) {
      let opts = {'event': 'step 1', 'locale': this.locale}
      window.dataLayer.push(opts)
      if (this.prefilledData.possible_complaint_guid) {
        this.prefillData()
      }
    }
    if (this.short_form) {
      this.$store.commit('round1/updateField', {path: 'complaint.preliminary', value: true})
    }
    // this.setUrlParams();
  },
  mounted() {
    setTimeout(() => {
      this.scrollToFormTop()
    }, 300)
  },
  methods: {
    async fetchPossibleComplaintData(guid = this.pc_guid) {
      try {
        const {data} = await axios.get(`/complaints/prefilled_data/${guid}`);
        this.prefilledData = data
      } catch (error) {
        console.error('fetchPossibleComplaint error:', error)
      }
    },
    isGfCase() {
      return this.source_origin == "gf_insurance";
    },
    scrollToFormTop() {
      let top_position = this.$device.mobile() ? 0 : this.$refs.formBlock?.offsetTop
      window.scrollTo({top: top_position, behavior: 'smooth'})
    },
    getAirport(id, path) {
      let which_airport = path

      return new Promise((resolve, reject) => {
        axios.get('/airports/' + id + '.json',
        ).then(({data}) => {
          this.$store.commit('round1/updateField', {
            path: `complaint.${which_airport}`,
            value: {id: data["id"], label: data["name"], value: data["name"]}
          })
          return resolve(data)
        }).catch((errors) => {
          return reject(errors)
        })
      })
    },
    getPossibleCompensation() {
      return new Promise((resolve, reject) => {
        axios.get('/possible_compensation', {
          params: {
            dep_apt_id: this.dep_apt,
            arr_apt_id: this.arr_apt,
            currency: this.defaultCurrency,
          }
        }).then(({data}) => {
          this.$store.commit('round1/updateField', {
            path: `compensation.amount`,
            value: data["amount"]
          })
          this.$store.commit('round1/updateField', {
            path: `compensation.currency`,
            value: data["currency"]
          })
          this.$store.commit('round1/updateField', {
            path: `compensation.which_law`,
            value: data["which_law"]
          })
          return resolve(data)
        }).catch((errors) => {
          return reject(errors)
        })
      })
    },
    prefillData() {
      //// Prefill Flight form
      const step1PrefilledKeys = ['departure_airport', 'arrival_airport', 'kind', 'reason', 'people', 'flight_number', 'possible_complaint_guid', 'cooperation_type', 'next_flight_number']
      step1PrefilledKeys.forEach(key => {
        this.$store.commit('round1/updateField', {path: `complaint.${key}`, value: this.prefilledData[key]});
      })

      const delay = Math.max(...Object.keys(this.delaysMapperForRegular).filter(key => Number(key) < this.prefilledData.delay).map(Number));
      this.$store.commit('round1/updateField', {path: `complaint.delay`, value: delay});

      let newDate = new Date(this.prefilledData.scheduled_arrival_at)
      let payload = {
        date: ('0' + newDate.getDate()).slice(-2),
        month: ('0' + (newDate.getMonth() + 1)).slice(-2),
        year: newDate.getFullYear()
      }
      this.$store.commit('round1/SET_SCHEDULED_ARRIVAL_AT_UI_DATE', payload)

      //// Prefill case form
      const step2PrefilledKeys = ['name', 'email', 'phone', 'phone_country_code', 'zip_code', 'address', 'city']
      step2PrefilledKeys.forEach(key => {
        this.$store.commit('round1/updateField', {path: `complaint.${key}`, value: this.prefilledData[key]});
      })
      this.$store.commit('round1/updateField', {path: `complaint.macda_country_id`, value: this.prefilledData.country})
      this.$store.commit('round1/updateField', {path: 'complaint.confirm_email_required', value: false});

      //// Prefill compensation info
      const compensationKeys = ['amount_in_eur', 'compensable', 'kind', 'currency', 'which_law']
      const {compensation} = this.prefilledData
      compensationKeys.forEach(key => {
        this.$store.commit('round1/updateField', {path: `compensation.${key}`, value: compensation[key]})
      })
      this.$store.commit('round1/updateField', {
        path: `compensation.amount`,
        value: compensation.passenger_compensation
      })

      if (this.step === 1 && compensation.compensable) {
        //// set step 2
        this.$store.commit('round1/updateField', {path: 'step', value: 2})
      }
    }
  }
}
</script>

<style lang="scss">
@import "~@stylesheets/_vars.scss";

.top-image-form {
  .h1 {
    text-align: center;
  }
}

.claim-page {
  position: relative;
  padding-top: 25px;
  padding-bottom: 30px;
  font-family: 'Montserrat';

  html.tablet &, html.desktop & {
    padding-top: 0;
    min-width: 768px;
    box-sizing: border-box;
    overflow: hidden;
    padding-right: 0;
    padding-bottom: 0;
  }

  h2 {
    font-weight: normal;
  }

  .title {
    font-size: 22px;
    text-align: center;
    margin-top: 30px;
    margin-bottom: 20px;

    html.tablet &, html.desktop & {
      text-align: left;
    }
  }

  .info-tip {
    margin-left: 5px;
    color: gray;
  }
}
</style>
